import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import MainLayout from './layouts/MainLayout';
import { Button } from '@/components/ui/button';
import FacilityGallery from '@/components/FacilityGallery';
import FacilityOverview from '@/components/FacilityOverview';
import SimilarFacilities from '@/components/SimilarFacilities';
import SearchFilters from '@/components/SearchFilters';
import Footer from '@/components/Footer';
import ChatModal from '@/components/ChatModal';
import OccupancyChart from '@/components/OccupancyChart';
import TourSlots from '@/components/TourSlots';

export default function FacilityDetails({ facility, user, searchResults = [], searchFilters = {} }) {
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [prefilledMessage, setPrefilledMessage] = useState('');
  // Handle the case where facility is not found
  if (!facility) {
    return (
      <MainLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center py-12">
            <p className="text-lg text-gray-500">Facility not found</p>
            <Link 
              href="/search" 
              className="inline-flex items-center mt-4 text-blue-600 hover:text-blue-800"
            >
              <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Search Results
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Prepare facility images
  const facilityImages = facility.images || [
    facility.image || "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
  ];
  
  // Handle back to search
  const handleBackToSearch = () => {
    router.visit('/search', {
      data: searchFilters,
    });
  };
  
  return (
    <MainLayout>
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb navigation */}
          <div className="mb-6">
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                <li className="inline-flex items-center">
                  <Link href="/" className="text-gray-600 hover:text-blue-600">
                    Home
                  </Link>
                </li>
                <li>
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <Link 
                      href="/search"
                      data={searchFilters}
                      className="text-gray-600 hover:text-blue-600"
                    >
                      Search
                    </Link>
                  </div>
                </li>
                <li aria-current="page">
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-500">{facility.name}</span>
                  </div>
                </li>
              </ol>
            </nav>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Search results sidebar - only shown if we have search results */}
            {searchResults && searchResults.length > 0 && (
              <div className="hidden lg:block lg:col-span-3">
                <div className="sticky top-6">
                  <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
                    <h2 className="text-lg font-semibold mb-4">Search Filters</h2>
                    <SearchFilters 
                      currentFilters={searchFilters} 
                      compact={true} 
                    />
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <h2 className="text-lg font-semibold mb-4">Search Results</h2>
                    <div className="space-y-4">
                      {searchResults.map((result) => (
                        <Link 
                          key={result.id} 
                          href={`/facilities/${result.slug}`}
                          data={searchFilters}
                          preserveState
                          only={['facility']}
                          className={`block p-3 rounded-lg border ${result.id === facility.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden">
                              <img 
                                src={result.image || "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"} 
                                alt={result.name} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{result.name}</p>
                              <p className="text-sm text-gray-500 truncate">{result.address}</p>
                              {result.cost && (
                                <p className="text-sm font-medium text-blue-600 mt-1">
                                  ${result.cost.toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                    
                    <Link 
                      href="/search"
                      data={searchFilters}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center justify-center mt-4"
                    >
                      View All Results
                      <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            )}
            
            {/* Main content */}
            <div className={`${searchResults && searchResults.length > 0 ? 'lg:col-span-9' : 'lg:col-span-12'}`}>
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Facility gallery */}
                <FacilityGallery images={facilityImages} />
                
                {/* Mobile back to search - only shown on mobile and if we have search results */}
                {searchResults && searchResults.length > 0 && (
                  <div className="lg:hidden p-4 border-b border-gray-200">
                    <Button 
                      variant="ghost" 
                      onClick={handleBackToSearch}
                      className="text-blue-600 hover:text-blue-800 flex items-center text-sm font-medium"
                    >
                      <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      Back to Search Results ({searchResults.length} communities)
                    </Button>
                  </div>
                )}
                
                {/* Facility overview */}
                <div className="p-6">
                  <FacilityOverview 
                    facility={facility} 
                    onContactClick={() => setChatModalOpen(true)}
                    onRequestInfo={(unitInfo) => {
                      const message = `I'm interested in the ${unitInfo.roomType} unit with ${unitInfo.bathrooms} bathroom(s) at ${facility.name}. Please provide more information about availability and pricing.`;
                      setPrefilledMessage(message);
                      setChatModalOpen(true);
                    }}
                  />
                  
                  {/* Premium features - Occupancy Chart */}
                  {facility.occupancy && (
                    <div className="mt-8">
                      <OccupancyChart occupancy={facility.occupancy} />
                    </div>
                  )}
                  
                  {/* Tour Slots */}
                  {facility.tour_slots && facility.tour_slots.length > 0 && (
                    <div className="mt-8">
                      <TourSlots tourSlots={facility.tour_slots} />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Similar facilities */}
              {searchResults && searchResults.length > 0 && (
                <div className="mt-8 bg-white rounded-xl shadow-sm p-6">
                  <SimilarFacilities 
                    facilities={searchResults.filter(r => r.id !== facility.id).slice(0, 3)} 
                    currentFilters={searchFilters}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      
      {/* Chat Modal */}
      <ChatModal 
        isOpen={chatModalOpen} 
        onClose={() => {
          setChatModalOpen(false);
          setPrefilledMessage('');
        }} 
        facility={facility}
        initialMessage={prefilledMessage}
      />
    </MainLayout>
  );
}
